import { useCallback, useEffect } from "react";
import { useNavigate, createFileRoute } from "@tanstack/react-router";
import { fetchAuthSession } from "aws-amplify/auth";
import {
  useGetRedirectPath,
  useOauthInitUser,
} from "@/components/providers/ApiProvider/hooks";
import {
  decodeOAuthProvider,
  pendingEmailVerificationRedirect,
} from "@/lib/auth";
import { getPersonaLandingPath } from "@/lib/post-login-redirect";
import { getRedirect, saveLastAuth } from "@/lib/storage-utils";
import type { LastAuthMethod } from "@/lib/storage-utils";
import { noindexHead } from "@/lib/seo";

export const Route = createFileRoute("/oauth2/idpresponse")({
  component: RouteComponent,
  head: () => noindexHead("Signing in | Lutely"),
});

function RouteComponent() {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const state = params.get("state");
  const provider = state ? decodeOAuthProvider(state) : "unknown";
  const oauthInit = useOauthInitUser();
  const getRedirectPath = useGetRedirectPath();

  const checkAuth = useCallback(async () => {
    try {
      // fetchAuthSession() triggers the code exchange in v6
      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken?.toString();
      const idToken = session.tokens?.idToken?.toString();

      if (accessToken && idToken && oauthInit.isIdle) {
        await oauthInit.mutateAsyncWithProvider(provider, {
          data: { accessToken, idToken },
        });

        // atob() breaks on JWT's base64url alphabet; Amplify already decoded it.
        const authedEmail =
          (session.tokens?.idToken?.payload?.email as string) ?? null;

        if (provider === "google" || provider === "apple") {
          saveLastAuth(provider as LastAuthMethod, authedEmail ?? "");
        }

        // Social sign-in is the easiest way to land in a session that
        // doesn't own the stashed code, so match before replaying.
        const pendingVerification =
          pendingEmailVerificationRedirect(authedEmail);
        if (pendingVerification) {
          window.location.replace(pendingVerification);
          return;
        }

        // Artists and label reps always land on /audiolab (unless an explicit
        // deep-link redirect is pending, which getRedirectPath honors).
        if (!getRedirect()) {
          const personaLanding = await getPersonaLandingPath();
          if (personaLanding) {
            navigate({ to: personaLanding, replace: true });
            return;
          }
        }

        const { pathname, search } = getRedirectPath();
        navigate({ to: pathname, search, replace: true });
      }
    } catch (error) {
      console.error("OAuth Exchange Error:", error);
      navigate({ to: "/", search: { auth: "login" as const } });
    }
  }, [navigate, oauthInit, provider, getRedirectPath]);

  useEffect(() => {
    checkAuth();
  }, []);
}
